import React, { MouseEvent, useMemo } from "react";
import { defineMessages, MessageDescriptor, useIntl } from "react-intl";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { AgeCategory } from "../../models/options";
import { TaxableAgeCategoryInput } from "../../models/cart";
import "./participants-input.scss";

const transDefs = defineMessages({
  [AgeCategory.ADULT]: { id: "guests-adult", defaultMessage: "Adultes" },
  [AgeCategory.BABY]: { id: "guests-baby", defaultMessage: "Bébés" },
  [AgeCategory.CHILD]: { id: "guests-child", defaultMessage: "Enfants" },
  [AgeCategory.SENIOR]: { id: "guests-senior", defaultMessage: "Séniors" },
  [AgeCategory.EXEMPT]: { id: "guests-exempt", defaultMessage: "Exemptés" },
});

type Props = {
  categories: { type: AgeCategory, helper?: MessageDescriptor, min?: number, max?: number, asExtra?: boolean }[];
  value: TaxableAgeCategoryInput[];
  onSelection: (value: TaxableAgeCategoryInput[]) => void;
  className?: string;
  max?: number;
}

const ParticipantsInput = (
  {
    categories,
    value,
    onSelection = () => void 0,
    className,
    max: mainMax
  }: Props) => {

  const intl = useIntl();
  const extraCats = useMemo(() => categories.filter(cat => !!cat.asExtra).map(cat => cat.type), [ categories ]);
  const count = useMemo(() => value.reduce((acc, val) => acc + (extraCats.includes(val.ageCategory) ? 0 : val.number), 0), [ value, extraCats ]);
  const asRecord = useMemo(() => value.reduce((acc, { ageCategory, number }) => ({ ...acc, [ageCategory]: number }), {}), [ value ]);

  const isButtonDisabled = (cat: AgeCategory, direction: 1 | -1, min?: number, max?: number, asExtra?: boolean) => {
    if (direction === -1 && (!asRecord[cat] ||asRecord[cat] === (min || 0))) return true;
    if (direction === 1 && asExtra && max === 0) return true;
    if (direction === 1 && typeof max !== "undefined" && asRecord[cat] === max) return true;
    if (direction === 1 && !!mainMax && !asExtra && count === mainMax) return true;
    return false;
  }

  const onClick = (e: MouseEvent, cat: AgeCategory, direction: -1 | 1) => {
    e.stopPropagation();
    onSelection([
      ...value.filter(({ ageCategory }) => cat !== ageCategory),
      ...[ { ageCategory: cat, number: (asRecord[cat] || 0) + direction }]
    ]);
  }

  const wrapperClasses = classNames("participants-input", className);

  return (
    <div className={wrapperClasses}>
      {categories.map(({ type: cat, helper, min, max, asExtra }, i) => (
        <div key={i} className="one-selector">
          <span className="title">{intl.formatMessage(transDefs[cat])}</span>
          {!!helper && <span className="helper">{`(${intl.formatMessage(helper)})`}</span>}
          <div className="count-stepper">
            <button type="button"
                    onClick={e => onClick(e, cat, -1)}
                    disabled={isButtonDisabled(cat, -1, min, max)}>
              <IconMinus />
            </button>
            <span className="val">{asRecord[cat] ?? 0}</span>
            <button type="button"
                    onClick={e => onClick(e, cat, 1)}
                    disabled={isButtonDisabled(cat, 1, min, max, asExtra)}>
              <IconPlus />
            </button>
          </div>
        </div>)
      )}
    </div>
  );

};

export default ParticipantsInput;
